/**
 * The due-date runway (§13.3) — "a horizontal 30-day strip; each card sits at
 * its due date, colored by urgency, check-marked once paid. The single most
 * useful glance in the app."
 *
 * Design choice worth defending: **the strip is positioned by date, not evenly
 * spaced.** Two bills three days apart look three days apart; a card due in
 * three weeks sits visibly far away. Evenly spacing them would be easier to
 * build and would destroy the one thing this component is for — seeing at a
 * glance whether your due dates are bunched or spread.
 *
 * The second choice: past-due bills whose payment can't be confirmed yet are
 * amber, not red. That is the normal state of every card's newest bill (a
 * payment only shows up on the *next* statement), so painting it red would make
 * the dashboard cry wolf every month. Red is reserved for bills MyDeck can
 * actually see are unpaid.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Check, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { Link } from "react-router-dom";

import { Panel, SectionTitle } from "@/components/ui/primitives";
import {
  statementsApi,
  type CardTheme,
  type CycleEvent,
  type RunwayEntry,
  type Urgency,
} from "@/lib/api/client";
import { cn } from "@/lib/cn";
import { addDaysISO, dayParts, formatDate, formatMoney } from "@/lib/format";

const URGENCY_DOT: Record<Urgency, string> = {
  overdue: "bg-overdue border-overdue",
  unconfirmed: "bg-warn border-warn",
  soon: "bg-warn border-warn",
  near: "bg-brass border-brass",
  later: "bg-raised border-strong",
  paid: "bg-paid border-paid",
};

export function urgencyLabel(urgency: Urgency, dayOffset: number): string {
  switch (urgency) {
    case "paid":
      return "Settled";
    case "overdue":
      return `${Math.abs(dayOffset)} day${Math.abs(dayOffset) === 1 ? "" : "s"} overdue`;
    case "unconfirmed":
      return "Awaiting confirmation";
    case "soon":
    case "near":
    case "later":
      if (dayOffset === 0) return "Due today";
      if (dayOffset === 1) return "Due tomorrow";
      return `Due in ${dayOffset} days`;
  }
}

/**
 * "I've paid this" — one click, from where the user notices.
 *
 * This exists because the alternative is a monthly chore. Every card's newest
 * bill sits unconfirmed until the following statement arrives, so without this
 * the user has to open each card's detail page to say something they already
 * know. The runway is where the question gets asked, so it is where the answer
 * belongs.
 *
 * **This is not a stopgap for Phase 6.** Alert parsing will confirm payments
 * only for issuers that actually send a payment confirmation, and in the sample
 * inbox just three of nine do, and the other six never will. One issuer's "we have received credits…" mail reads exactly like a
 * payment confirmation and is not: it announces a *cashback* credit, then asks
 * for the remaining balance. Which issuers do what is recorded in
 * docs/EMAIL-FORMATS.md, not here — §2.1 keeps bank names out of app code.
 *
 * Shown only for bills that are actually in question. A settled or not-yet-due
 * bill gets no button, because pre-emptively marking a future bill paid is not
 * a thing anyone means to do.
 */
function MarkPaid({ entry }: { entry: RunwayEntry }) {
  const qc = useQueryClient();

  const mark = useMutation({
    mutationFn: () => {
      if (!entry.statement_id) throw new Error("No statement to mark.");
      return statementsApi.setPaymentStatus(entry.statement_id, "paid");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      qc.invalidateQueries({ queryKey: ["statements"] });
      toast.success(`${entry.nickname ?? "Bill"} marked paid.`);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const answerable =
    entry.statement_id && (entry.urgency === "unconfirmed" || entry.urgency === "overdue");

  // The cell is always rendered so rows stay aligned whether or not it applies.
  return (
    <span className="col-start-3 row-start-2 shrink-0 text-right sm:w-20">
      {answerable && (
        <button
          type="button"
          onClick={() => mark.mutate()}
          disabled={mark.isPending}
          title="Tell MyDeck this bill is settled. It stays your answer until you change it."
          className="inline-flex items-center gap-1 rounded-lg border border-hairline px-2 py-1 text-xs text-muted transition-colors hover:border-paid/40 hover:bg-paid/10 hover:text-paid focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brass/50 disabled:opacity-50"
        >
          {mark.isPending ? (
            <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
          ) : (
            <Check className="h-3 w-3" aria-hidden />
          )}
          Paid
        </button>
      )}
    </span>
  );
}

export function DueRunway({
  entries,
  events = [],
  days,
  today,
}: {
  entries: RunwayEntry[];
  /** Cycle closes, drawn as thin ticks so the strip shows the whole month. */
  events?: CycleEvent[];
  days: number;
  today: string;
}) {
  if (entries.length === 0) {
    return (
      <Panel className="p-6">
        <SectionTitle>Due runway</SectionTitle>
        <p className="mt-2 text-sm text-muted">
          No due dates in the next {days} days. MyDeck places each card here once its
          statement carries a payment due date.
        </p>
      </Panel>
    );
  }

  // Positioned by DATE, one cell per day: two bills three days apart look three
  // days apart. The window opens at the earliest bill still in question (so an
  // overdue or unconfirmed one is visible, not clipped) and runs at least two
  // weeks ahead — further if a bill sits further out.
  const offsets = entries.map((e) => e.day_offset);
  const from = Math.max(-21, Math.min(0, ...offsets) - 1);
  const to = Math.min(days, Math.max(14, ...offsets, ...events.map((e) => e.day_offset)) + 1);
  const cells = Array.from({ length: to - from + 1 }, (_, i) => from + i);

  const closesOn = new Map<number, CycleEvent[]>();
  for (const e of events) {
    if (e.kind !== "closes") continue;
    closesOn.set(e.day_offset, [...(closesOn.get(e.day_offset) ?? []), e]);
  }
  const duesOn = new Map<number, RunwayEntry[]>();
  for (const e of entries) duesOn.set(e.day_offset, [...(duesOn.get(e.day_offset) ?? []), e]);

  const sorted = [...entries].sort((a, b) => a.day_offset - b.day_offset);

  return (
    <Panel className="p-5 sm:p-6">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <SectionTitle>Due runway</SectionTitle>
        <Link to="/cycles" className="label-micro transition-colors hover:text-ink">
          All cycles →
        </Link>
      </div>

      {/* The strip. Decorative — the list below carries every fact, so screen
          readers get the list and the strip is hidden from them. */}
      <div className="-mx-1 mt-5 overflow-x-auto pb-2" aria-hidden>
        <div
          className="grid gap-1 px-1"
          style={{ gridTemplateColumns: `repeat(${cells.length}, minmax(1.75rem, 1fr))` }}
        >
          {cells.map((offset, i) => {
            const iso = addDaysISO(today, offset);
            const { day, weekday, month } = dayParts(iso);
            const dues = duesOn.get(offset) ?? [];
            const closes = closesOn.get(offset) ?? [];
            const isToday = offset === 0;
            const weekend = weekday === 0 || weekday === 6;
            return (
              <div
                key={offset}
                className={cn(
                  "relative flex h-[5.5rem] animate-rise flex-col items-center rounded-lg border pt-1.5 font-mono text-[10.5px] transition-colors duration-300 hover:bg-raised",
                  isToday
                    ? "border-brass/60 bg-brass/[0.08] text-brass"
                    : "border-transparent text-faint",
                  !isToday && (weekend ? "bg-raised/30" : "bg-raised/60"),
                  offset < 0 && "opacity-60",
                )}
                style={{ animationDelay: `${200 + i * 14}ms` }}
                title={[
                  formatDate(iso),
                  ...dues.map((d) => `${d.nickname ?? d.last4} — ${formatMoney(d.amount)} — ${urgencyLabel(d.urgency, d.day_offset)}`),
                  ...closes.map((c) => `${c.nickname ?? c.last4} — cycle closes`),
                ].join("\n")}
              >
                <span className="text-[9px] opacity-70">{"SMTWTFS"[weekday]}</span>
                <span className={cn(isToday && "font-semibold")}>{day}</span>
                {day === 1 && <span className="text-[8.5px] uppercase opacity-80">{month}</span>}
                <div className="absolute inset-x-[3px] bottom-1.5 flex flex-col gap-[3px]">
                  {dues.map((d) => (
                    <span
                      key={d.card_id}
                      className={cn(
                        "relative block h-3 rounded-[3px] shadow-[inset_0_1px_0_rgb(255_255_255/0.25)]",
                        (d.urgency === "unconfirmed") && "opacity-55 outline-dashed outline-1 -outline-offset-1 outline-white/40",
                        d.urgency === "overdue" && "outline outline-1 outline-offset-1 outline-overdue",
                        (d.urgency === "soon") && "outline outline-1 outline-offset-1 outline-warn/70",
                      )}
                      style={{ backgroundImage: swatch(d.theme) }}
                    >
                      {d.urgency === "paid" && (
                        <Check className="absolute inset-0 m-auto h-2.5 w-2.5 text-white" strokeWidth={3} />
                      )}
                    </span>
                  ))}
                  {closes.map((c) => (
                    <span key={`c-${c.card_id}`} className="block h-[3px] rounded-full bg-ink/25" />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1.5 text-[11.5px] text-faint" aria-hidden>
        <span className="flex items-center gap-2"><i className="h-2.5 w-4 rounded-[3px] bg-ink/40" />Bill due</span>
        <span className="flex items-center gap-2"><i className="h-2.5 w-4 rounded-[3px] bg-ink/20 outline-dashed outline-1 -outline-offset-1 outline-ink/40" />Awaiting confirmation</span>
        <span className="flex items-center gap-2"><i className="h-[3px] w-4 rounded-full bg-ink/25" />Cycle closes</span>
      </div>

      {/* The list carries the actual information; the strip carries the shape. */}
      <ul className="mt-5 divide-y divide-hairline border-t border-hairline">
        {sorted.map((e) => (
          <li
            key={e.card_id}
            className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-3 gap-y-2 py-3 sm:flex sm:gap-x-4"
          >
            <span
              aria-hidden
              className="h-[22px] w-[34px] shrink-0 rounded-[5px] shadow-[inset_0_1px_0_rgb(255_255_255/0.25)]"
              style={{ backgroundImage: swatch(e.theme) }}
            />
            <Link
              to={`/cards/${e.card_id}`}
              className="min-w-0 flex-1 truncate text-sm text-ink transition-colors hover:text-brass"
            >
              {e.nickname ?? `•••• ${e.last4}`}
              <span className="ml-2 hidden font-mono text-xs text-faint sm:inline">•{e.last4}</span>
            </Link>
            <span className="hidden shrink-0 text-sm tabular-nums text-muted sm:block">
              {formatDate(e.due_date)}
            </span>
            <span className="col-start-2 row-start-2 shrink-0 sm:w-44">
              <UrgencyChip entry={e} />
            </span>
            <span className="col-start-3 row-start-1 shrink-0 text-right text-sm font-medium tabular-nums text-ink sm:w-28">
              {formatMoney(e.amount)}
            </span>
            <MarkPaid entry={e} />
          </li>
        ))}
      </ul>
    </Panel>
  );
}

function UrgencyChip({ entry }: { entry: RunwayEntry }) {
  const label =
    entry.urgency === "paid" ? "Settled" : urgencyLabel(entry.urgency, entry.day_offset);
  const tone: Record<Urgency, string> = {
    overdue: "border-overdue/30 bg-overdue/10 text-overdue",
    unconfirmed: "border-dashed border-hairline-strong text-muted",
    soon: "border-warn/30 bg-warn/10 text-warn",
    near: "border-brass/25 bg-brass/10 text-brass",
    later: "border-hairline text-muted",
    paid: "border-paid/25 bg-paid/10 text-paid",
  };
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs",
        tone[entry.urgency],
      )}
    >
      {entry.urgency === "paid" ? (
        <Check className="h-3 w-3" aria-hidden />
      ) : (
        <span aria-hidden className={cn("h-1.5 w-1.5 rounded-full", URGENCY_DOT[entry.urgency])} />
      )}
      {label}
    </span>
  );
}

function swatch(theme: CardTheme | null): string {
  return theme
    ? `linear-gradient(140deg, ${theme.gradient_from}, ${theme.gradient_to})`
    : "linear-gradient(140deg, #3a3a40, #26262a)";
}
